import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Calendar, MapPin, Users, CheckCircle2, ChevronLeft, ChevronRight,
  Sparkles, Clock, Phone, Mail, User, PartyPopper, Loader2, Building2, Shield,
} from "lucide-react";

const TIPOS = ["Boda civil", "XV años", "Cumpleaños", "Cumpleaños infantil", "Bautizo", "Primera comunión", "Aniversario", "Empresarial", "Otro"];
const HOY = new Date().toISOString().slice(0, 10);

const ReservarPetit = () => {
  const params = useParams();
  // Detecta unidad por URL: /reservar/petit, /reservar/kermesita, /reservar/bocatto. Default petit.
  const unidad = (params.unidad || params.unidadSlug || "petit").toLowerCase();
  const [info, setInfo] = useState(null);
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [dispo, setDispo] = useState({});
  const [fecha, setFecha] = useState("");
  const [step, setStep] = useState(1);
  const [enviando, setEnviando] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    nombre: "", whatsapp: "", correo: "",
    tipo_evento: "Boda civil",
    adultos: 50, jovenes: 0, ninos: 0, bebes: 0,
  });

  const cargarDispo = useCallback(async () => {
    try {
      const [{ data: d }, { data: i }] = await Promise.all([
        axios.get(`${API}/publico/reservas/disponibilidad`, { params: { mes, unidad } }),
        info ? Promise.resolve({ data: info }) : axios.get(`${API}/publico/${unidad}/info`),
      ]);
      setDispo(d.dias || {});
      if (!info) setInfo(i);
    } catch { /* noop */ }
  }, [mes, info, unidad]);

  useEffect(() => { cargarDispo(); }, [cargarDispo]);

  const dias = useMemo(() => {
    const [y, m] = mes.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    const firstDow = new Date(y, m - 1, 1).getDay();
    const arr = Array(firstDow).fill(null);
    for (let d = 1; d <= last; d++) arr.push(`${mes}-${String(d).padStart(2, "0")}`);
    return arr;
  }, [mes]);

  const cambiarMes = (delta) => {
    const [y, m] = mes.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMes(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const enviar = async () => {
    if (!fecha) { toast.error("Selecciona una fecha"); return; }
    if (!form.nombre || !form.whatsapp) { toast.error("Nombre y WhatsApp son obligatorios"); return; }
    setEnviando(true);
    try {
      const { data } = await axios.post(`${API}/publico/reservas/apartar`, { ...form, fecha, unidad });
      setSuccess(data);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo apartar la fecha");
    } finally { setEnviando(false); }
  };

  const COLOR_ESTADO = {
    contratado: { bg: "bg-emerald-600 text-white", label: "Contratado" },
    apartado: { bg: "bg-amber-400 text-amber-950", label: "Apartado" },
    bloqueado: { bg: "bg-slate-400 text-slate-50", label: "No disponible" },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-stone-50 to-amber-50">
      {/* Galería hero */}
      {info?.fotos && info.fotos.length > 0 && (
        <div className="bg-stone-900">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-1 p-1">
            {info.fotos.slice(0, 4).map((url, i) => (
              <div key={`foto-${i}-${url}`} className={`${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-square"} bg-stone-200 overflow-hidden`}>
                <img src={url.startsWith("http") ? url : `${API.replace("/api", "")}${url}`} alt={`${info.nombre} ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hero */}
      <header className="bg-emerald-900 text-stone-50 py-10 px-4 text-center" style={{ backgroundImage: "linear-gradient(135deg,#064e3b 0%,#0f766e 100%)" }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-emerald-200 uppercase text-xs tracking-[0.3em] mb-2">Reserva tu fecha</p>
          <h1 className="font-display text-4xl sm:text-5xl mb-3">{info?.nombre || "CelebraMid"}</h1>
          <p className="text-emerald-100 text-base sm:text-lg max-w-2xl mx-auto">{info?.descripcion || "Salón íntimo perfecto para tu evento."}</p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-sm text-emerald-100">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {info?.ubicacion || "—"}</span>
            <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {info?.capacidad || "—"}</span>
            {info?.precio_base && <span className="flex items-center gap-1"><Sparkles className="w-4 h-4" /> Desde ${Number(info.precio_base).toLocaleString("es-MX")} {info.moneda || "MXN"}</span>}
            <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> Apartado 24h gratis</span>
          </div>
          {info?.owner_user_id && (
            <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center items-center">
              <a
                href={`/cotizar/${info.owner_user_id}`}
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-semibold px-5 py-2.5 rounded-full transition-all shadow-md"
                data-testid="btn-ir-cotizador"
              >
                <Sparkles className="w-4 h-4" />
                Arma tu evento ideal con nuestro cotizador
              </a>
              <span className="text-emerald-200 text-xs">o aparta tu fecha aquí abajo ↓</span>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-6">
        {/* Calendario */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-emerald-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl text-stone-900 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-emerald-700" /> Selecciona tu fecha
                </h2>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => cambiarMes(-1)} data-testid="mes-prev"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm font-medium px-3 capitalize">
                    {(() => {
                      const [yy, mm] = mes.split("-").map(Number);
                      return new Date(yy, mm - 1, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
                    })()}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => cambiarMes(1)} data-testid="mes-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>

              {/* Leyenda */}
              <div className="flex flex-wrap gap-3 text-xs text-stone-600 mb-3">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-stone-300" /> Disponible</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" /> Apartado</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-600" /> Contratado</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-400" /> No disponible</span>
              </div>

              {/* Calendario */}
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase text-stone-400 mb-1">
                {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {dias.map((d, i) => {
                  if (!d) return <div key={`empty-${i}`} />;
                  const num = d.split("-")[2];
                  const estado = dispo[d];
                  const pasado = d < HOY;
                  const seleccionado = d === fecha;
                  let cls = "border-stone-200 bg-white hover:bg-emerald-50 cursor-pointer text-stone-900";
                  if (pasado) cls = "border-stone-100 bg-stone-50 text-stone-300 cursor-not-allowed";
                  else if (estado === "contratado") cls = `${COLOR_ESTADO.contratado.bg} border-transparent cursor-not-allowed`;
                  else if (estado === "apartado") cls = `${COLOR_ESTADO.apartado.bg} border-transparent cursor-not-allowed`;
                  else if (estado === "bloqueado") cls = `${COLOR_ESTADO.bloqueado.bg} border-transparent cursor-not-allowed`;
                  if (seleccionado) cls = "border-2 border-emerald-700 bg-emerald-100 text-emerald-900 ring-2 ring-emerald-300";
                  const disabled = pasado || !!estado;
                  return (
                    <button
                      key={d}
                      disabled={disabled}
                      onClick={() => { setFecha(d); setStep(2); document.getElementById("form-reserva")?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                      className={`aspect-square rounded-lg border text-sm font-semibold transition ${cls}`}
                      data-testid={`dia-${d}`}
                      title={estado ? `${COLOR_ESTADO[estado]?.label || estado}` : "Disponible"}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Form reserva */}
          {step >= 2 && (
            <Card id="form-reserva" className="border-amber-300 shadow-md">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl text-stone-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-600" /> Apartar el {fecha}
                  </h3>
                  <span className="text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded font-medium">Gratis por 24h</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label><User className="w-3 h-3 inline mr-1" />Tu nombre *</Label>
                    <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="res-nombre" />
                  </div>
                  <div>
                    <Label><Phone className="w-3 h-3 inline mr-1" />WhatsApp *</Label>
                    <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="999XXXXXXX" data-testid="res-whatsapp" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label><Mail className="w-3 h-3 inline mr-1" />Correo (opcional)</Label>
                    <Input type="email" value={form.correo} onChange={(e) => setForm({ ...form, correo: e.target.value })} data-testid="res-correo" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label><PartyPopper className="w-3 h-3 inline mr-1" />Tipo de evento</Label>
                    <Select value={form.tipo_evento} onValueChange={(v) => setForm({ ...form, tipo_evento: v })}>
                      <SelectTrigger data-testid="res-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>{TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <Label className="text-emerald-900 font-semibold">¿Cuántas personas asistirán?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                    <div><Label className="text-xs">Adultos</Label><Input type="number" min="0" value={form.adultos} onChange={(e) => setForm({ ...form, adultos: Number(e.target.value) || 0 })} data-testid="res-adultos" /></div>
                    <div><Label className="text-xs">Jóvenes</Label><Input type="number" min="0" value={form.jovenes} onChange={(e) => setForm({ ...form, jovenes: Number(e.target.value) || 0 })} data-testid="res-jovenes" /></div>
                    <div><Label className="text-xs">Niños</Label><Input type="number" min="0" value={form.ninos} onChange={(e) => setForm({ ...form, ninos: Number(e.target.value) || 0 })} data-testid="res-ninos" /></div>
                    <div><Label className="text-xs">Bebés</Label><Input type="number" min="0" value={form.bebes} onChange={(e) => setForm({ ...form, bebes: Number(e.target.value) || 0 })} data-testid="res-bebes" /></div>
                  </div>
                </div>
                <Button onClick={enviar} disabled={enviando} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white h-12 text-base" data-testid="btn-apartar">
                  {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                  Apartar esta fecha gratis por 24h
                </Button>
                <p className="text-xs text-stone-500 text-center">
                  Nuestro equipo te contactará por WhatsApp en menos de 24 horas para confirmar tu reserva y enviarte el contrato. Sin compromiso hasta firma.
                </p>
              </CardContent>
            </Card>
          )}

          {step === 3 && success && (
            <Card className="border-emerald-500 bg-emerald-50">
              <CardContent className="p-6 text-center">
                <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600 mb-3" />
                <h3 className="font-display text-2xl text-emerald-900">¡Fecha apartada con éxito!</h3>
                <p className="text-stone-700 mt-2">
                  Apartamos el <strong>{success.fecha}</strong> por 24 horas a tu nombre.<br />
                  En las próximas horas te contactaremos por WhatsApp para confirmar y enviarte tu cotización personalizada.
                </p>
                <p className="text-xs text-stone-500 mt-3">Folio: {success.evento_id}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Lateral: incluye + políticas */}
        <div className="space-y-4">
          <Card className="border-stone-200">
            <CardContent className="p-5">
              <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-emerald-700" /> ¿Qué incluye?
              </h3>
              <ul className="space-y-1.5 text-sm text-stone-700">
                {(info?.incluye || []).map((it, i) => (
                  <li key={`inc-${i}-${(it || "").slice(0,6)}`} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-5">
              <h3 className="font-display text-lg text-stone-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-700" /> Políticas
              </h3>
              <ul className="space-y-1.5 text-sm text-stone-700">
                {(info?.politicas || []).map((p, i) => (
                  <li key={`pol-${i}-${(p || "").slice(0,6)}`} className="flex items-start gap-2">
                    <Clock className="w-3 h-3 text-stone-500 mt-1 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <footer className="text-center py-6 text-xs text-stone-500">
        Powered by CelebraMid · Reserva segura · Tus datos están protegidos
      </footer>
    </div>
  );
};

export default ReservarPetit;
